import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { isHandlerSite } from '../config/siteMode';
import { COBALT, FONT_DISPLAY, FONT_MONO } from '../theme/kiff';

/**
 * Catch-all page for unknown URLs.
 *
 * Without a `path="*"` route, React Router matched nothing and rendered a bare
 * navbar over an empty page — indistinguishable from a broken app, and it
 * returned HTTP 200 to crawlers, so dead URLs stayed in the index.
 *
 * The "back to safety" link is role-aware: sending a signed-in handler to
 * the owner dashboard would just bounce them again.
 */
export default function NotFound() {
  const { isAuthenticated, user } = useAuth();
  const { pathname } = useLocation();

  const homePath = !isAuthenticated
    ? '/'
    : user?.is_staff
      ? '/admin/dashboard'
      : isHandlerSite
        ? '/handler/dashboard'
        : '/owner/dashboard';

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          textAlign: 'center',
          py: { xs: 8, md: 14 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontFamily: FONT_DISPLAY,
            fontWeight: 900,
            fontStretch: '110%',
            fontSize: { xs: 96, md: 128 },
            lineHeight: 1,
            color: COBALT,
            letterSpacing: '-0.02em',
          }}
        >
          404
        </Typography>

        <Typography variant="h1" component="h2">
          This page ran off the lead
        </Typography>

        <Typography color="text.secondary" sx={{ maxWidth: 420 }}>
          The link may be broken, or the page may have moved. Nothing has
          happened to your bookings or your pets.
        </Typography>

        {/* Useful when someone pastes a mistyped link into a support chat. */}
        <Typography
          sx={{ fontFamily: FONT_MONO, fontSize: 12, color: 'text.disabled', wordBreak: 'break-all' }}
        >
          {pathname}
        </Typography>

        <Button
          component={RouterLink}
          to={homePath}
          variant="contained"
          size="large"
          sx={{ mt: 2 }}
        >
          Back to safety
        </Button>
      </Box>
    </Container>
  );
}
